<template>
  <div class="relative map-wrap">
    <div id="search-result-map" class="w-full h-full pc:rounded-[20px]"/>
    <Transition name="slide-fade">
      <MobileMapSpaceList v-if="isMobile"
                          ref="refMobileMapSpaceList"
                          :company-list=spaceList
                          @onCompanySelected="refreshSelectedMarker"/>
    </Transition>
    <!-- 桌機:地圖頂部控制列 -->
    <div v-if="!isMobile"
         class="map-top-controls">
      <label class="map-move-toggle">
        <input v-model="searchOnMove"
               type="checkbox">
        <span>{{ $t('map_list.search_on_move') }}</span>
      </label>
      <div class="map-research-btn"
           @click="geoSearch">
        {{ $t('common.re_search') }}
      </div>
    </div>

    <!-- 手機:重新搜尋此區域 -->
    <div v-if="isMobile"
         class="re-search"
         @click="geoSearch">
      {{ $t('common.re_search') }}
    </div>

    <!-- 桌機:列表檢視 -->
    <div v-if="!isMobile"
         class="map-list-view"
         @click="emit('toList')">
      <svg-icon name="to_list"
                size="20"
                class="mr-[4px]" />
      {{ $t('map_list.list_view') }}
    </div>
  </div>
</template>

<script setup>

import {computed, onBeforeMount, reactive, ref, toRaw, watch} from "vue";
import {useSettingStore} from "@/stores/setting.js";
import {useSearchResultStore} from "~/stores/searchResult.ts";
import {useRoute, useRouter} from "nuxt/app";
import {useSearchQueryStore} from "@/stores/searchQuery.js";
import {useSearchWithStore} from "@/composables/useSearchWithStore";
import MobileMapSpaceList from "@/components/MobileMapSpaceList.vue";
import {parseFormatCheapestRentPrice} from "~/utils/newPriceTool.ts";

const emit = defineEmits(['toList'])

const { $loadGoogleMaps } = useNuxtApp()
const router = useRouter()
const route = useRoute()
const {handleSearchWithStore} = useSearchWithStore()

// 搜尋時移動地圖:勾選後拖曳/縮放地圖即自動重新搜尋(預設關閉,維持原本手動行為)
const searchOnMove = ref(false)
let mapMoveListeners = []
watch(searchOnMove, (enabled) => {
  if (!states.map) {
    return
  }
  if (enabled) {
    mapMoveListeners.push(states.map.addListener('dragend', geoSearch))
    mapMoveListeners.push(states.map.addListener('zoom_changed', geoSearch))
  } else {
    mapMoveListeners.forEach((listener) => listener.remove())
    mapMoveListeners = []
  }
})
const settingStore = useSettingStore()
const searchResultStore = useSearchResultStore()
const searchQueryStore = useSearchQueryStore()

const refMobileMapSpaceList = ref(null)

const isMobile = computed(() => settingStore.isMobile);
/// 搜尋結果列表
const spaceList = computed(() => searchResultStore.getSpaceList);
/// 選中空間 space_id
const routeSid = computed(() => route.query.msid)


const states = reactive({
  map: null,
  markers: []
});

// 確保 initMap 只被 call 一次
let mapInitialized = false;
onBeforeMount(async () => {
  if(process.client && !mapInitialized){
    await initMap()
    mapInitialized = true
  }
})


const initMap = async () => {
  try {
    const googleMaps = await $loadGoogleMaps()
    const { Map } = await googleMaps.importLibrary('maps')

    const mapOptions = {
      mapId: 'DEMO_MAP_ID',
      center: { lat: 25.044351, lng: 121.516038 },
      zoom: 14,
      maxZoom: 18,
      minZoom: 4,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: !isMobile,
    }

    states.map = new Map(document.getElementById('search-result-map'), mapOptions)
  } catch (error) {
    console.error('Google Maps 初始化失敗', error)
  }
};

async function geoSearch() {
  const {latitude, longitude, width, height} = await getMapBounds();
  searchQueryStore.setGeoSearch({
    longitude: longitude,
    latitude: latitude,
    width: width,
    height: height
  })
  searchQueryStore.clearCitySelected()
  searchQueryStore.setPagination({sort: 'location', dir: 0})

  await handleSearchWithStore()
}

const getMapBounds = async () => {
  let latitude = route.query.latitude || '25.044351';
  let longitude = route.query.longitude || '121.516038';
  let width = 9000;
  let height = 9000;
  const bounds = await states.map.getBounds();

  if (bounds) {
    const center = states.map.getCenter(); // 中心點
    const {spherical} = await google.maps.importLibrary("geometry");
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    // 這兩點的緯度相同，所以寬度就是這兩點之間的經度差的距離
    width = spherical.computeDistanceBetween(
        new google.maps.LatLng(southWest.lat(), southWest.lng()),
        new google.maps.LatLng(southWest.lat(), northEast.lng())
    );

    // 這兩點的經度相同，所以高度就是這兩點之間的緯度差的距離
    height = spherical.computeDistanceBetween(
        new google.maps.LatLng(southWest.lat(), southWest.lng()),
        new google.maps.LatLng(northEast.lat(), southWest.lng())
    );

    latitude = center.lat();
    longitude = center.lng();
  }

  return {latitude, longitude, width, height};
}

watch(() => [states.map, spaceList.value], () => {
  if (states.map) {
    addMarker()
  }
})

async function addMarker() {
  const {AdvancedMarkerElement} = await google.maps.importLibrary("marker");
  cleanMarkers()

  spaceList.value?.forEach((space, index) => {
    /// 加入標記
    const priceTag = document.createElement("div");
    priceTag.className = "price-tag";
    priceTag.textContent = parseFormatCheapestRentPrice(space.rent.rent_groups)
    priceTag.dataset.msid = space.space_id.toString();
    const marker = new AdvancedMarkerElement({
      map: toRaw(states.map),
      position: {lat: parseFloat(space.latitude), lng: parseFloat(space.longitude)},
      title: space.admin_id.toString(),
      content: priceTag,
      zIndex: 1 // 初始 zIndex
    });

    /// 點擊事件
    marker.addListener("click", () => {
      marker.zIndex = 100; // 設定最高 zIndex
      if (isMobile.value) {
        refMobileMapSpaceList.value.slideTo(index)
      } else {
        // 點擊標記，網址加上 sid (開啟空間詳情)
        router.replace({query: {'msid': space.space_id, 'slideTo': true}})
      }
    });

    /// 新增標記
    states.markers.push(marker);
  })

  /// 根據標記，顯示地圖範圍
  const bounds = new google.maps.LatLngBounds();
  spaceList.value?.map(space => {
    return new google.maps.LatLng(space.latitude, space.longitude)
  }).forEach(function (latLng) {
    bounds.extend(latLng);
  });
  if (spaceList.value?.length > 0) {
    states.map.fitBounds(bounds, getPaddingOptions());
  }
}

/// 連動網址 msid 選中 marker
watch(() => routeSid.value, () => {
  refreshSelectedMarker()
})

function refreshSelectedMarker(index = -1) {
  states.markers.forEach((marker, i) => {
    if (toRaw(marker).content.dataset.msid === routeSid.value || i === index) {
      toRaw(marker).content.classList.add('price-tag-selected')
      toRaw(marker).zIndex = 100;
      panToPosition(toRaw(marker).position);
    } else {
      toRaw(marker).content.classList.remove('price-tag-selected')
      toRaw(marker).zIndex = 1;
    }
  })
}

function panToPosition(position) {
  /// todo 手機版位移調整
  const paddingOptions = getPaddingOptions()

  // states.map.setZoom(14)
  states.map.panTo(position);
  states.map.panBy((paddingOptions.right - paddingOptions.left) / 2, (paddingOptions.bottom - paddingOptions.top) / 2);
}

function getPaddingOptions() {
  return isMobile.value ?
      {
        top: 0,
        bottom: window.screen.width * 0.57,
        left: 0,
        right: 0
      } :
      {
        top: 0,
        bottom: 0,
        left: routeSid.value ? 0 : 0,
        right: 0
      }
}

function cleanMarkers() {
  states.markers.forEach(marker => {
    toRaw(marker).setMap(null);
  })
  states.markers.length = 0
}
</script>

<style>
.price-tag {
  @apply bg-white rounded-[8px] text-black text-center text-[16px] px-[12px] py-[6px] cursor-pointer border-osn-black-00 border-[2px];
}

.price-tag-selected {
  @apply bg-osn-yellow-00 font-bold;
}

</style>

<style scoped lang="scss">
/*點擊地圖會有藍框*/
:deep(.gm-style iframe + div) {
  border: none !important;
}

.map-wrap {
  @apply absolute top-0 bottom-0 left-0 right-0 rounded-[20px];
}

.re-search {
  @apply absolute bottom-[40px] left-1/2 translate-x-[-50%] min-w-[103px] h-[36px] leading-[36px] px-[30px] rounded-[48px] text-black text-center bg-osn-white-00 cursor-pointer
  mobile:bottom-[240px];
}

.map-top-controls {
  @apply absolute top-[12px] right-[12px] flex items-center gap-[8px] z-10;

  .map-move-toggle {
    @apply flex items-center gap-[6px] px-[10px] h-[34px] text-[13px] text-osn-dark-grey-00
    bg-osn-white-00 rounded-[8px] shadow-md cursor-pointer;

    input {
      @apply w-[15px] h-[15px] cursor-pointer accent-osn-yellow-00;
    }
  }

  .map-research-btn {
    @apply flex items-center px-[14px] h-[34px] text-[13px] text-osn-dark-grey-00
    bg-osn-white-00 rounded-[8px] shadow-md cursor-pointer;
  }
}

.map-list-view {
  @apply absolute bottom-[16px] right-[16px] flex items-center px-[14px] h-[36px] text-[14px]
  text-osn-dark-grey-00 bg-osn-white-00 rounded-[8px] shadow-md cursor-pointer z-10;
}
</style>
